import { createContext, useContext } from 'react';
import type { FeatureKey, FeatureView, Membership, Role, SchoolProfile, SessionUser } from '@/lib/api/types';

/** Who is signed in, where, and what is switched on. Loaded once by the app shell. */
export interface AppContextValue {
  user: SessionUser;
  isPlatformAdmin: boolean;
  memberships: Membership[];
  membership: Membership | null;
  role: Role | null;
  /** null when the session is not scoped to a school (platform admin). */
  school: SchoolProfile | null;
  features: Record<FeatureKey, boolean>;
  featureList: FeatureView[];
}

export const AppContext = createContext<AppContextValue | null>(null);

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp outside the signed-in app');
  return ctx;
}

export function useOptionalApp(): AppContextValue | null {
  return useContext(AppContext);
}

/** The school of the current session; only use inside school-scoped screens. */
export function useSchool(): SchoolProfile {
  const school = useApp().school;
  if (!school) throw new Error('useSchool without a school-scoped session');
  return school;
}
